import { IListFilters } from '../../pages/Dashboard/Users/models'
import apiService from '../Api'
import { ISector } from '../Sector/SectorService'

export interface IUser {
  id: number
  name: string
  email: string
  picture: string
  pending_approval: boolean
  roles: string[]
  user_group_id: number
  sectors: ISector[] | null
  is_senior: boolean
  restricted: boolean
  login_method: 'email' | 'google' | null
}

export interface IList {
  current_page: number
  per_page: number
  total: number
  total_selected: number
  data: IUser[]
}

export interface ICreateUser {
  name: string
  email: string
  password: string
  password_confirmation: string
  picture: File
  user_group_id: number
  sector_ids: number[]
  is_senior: boolean
}

export interface IUpdateUser {
  name: string
  email: string
  password?: string
  password_confirmation?: string
  picture?: File | string
  user_group_id: number
  sector_ids: number[]
  is_senior: boolean
}

export interface IMeData {
  id: string
  name: string
  email: string
  picture: string
  roles: string[]
  // Consultor comercial que esta conta representa, se houver (ABRA-140).
  commercial_consultant_id: number | null
}

export class UserService {
  async list(filters: { page?: number; pageSize?: number; filter: IListFilters }): Promise<IList> {
    return apiService
      .get('/user', {
        params: {
          page: filters.page ?? 1,
          page_size: filters.pageSize ?? 10,
          status: filters.filter.status,
          group: filters.filter.group,
          search: filters.filter.search,
          sector: filters.filter.sector
        }
      })
      .then((response) => response.data)
  }

  async create(data: ICreateUser): Promise<IUser> {
    const formData = new FormData()
    formData.append('name', data.name)
    formData.append('email', data.email)
    formData.append('password', data.password)
    formData.append('password_confirmation', data.password_confirmation)
    formData.append('picture', data.picture)
    formData.append('user_group_id', data.user_group_id.toString())
    formData.append('is_senior', data.is_senior ? '1' : '0')

    for (const sector_id of data.sector_ids) {
      formData.append('sector_ids[]', sector_id.toString())
    }

    return apiService.post('/user', formData).then((response) => response.data)
  }

  async update(userId: number, data: IUpdateUser, updatePassword: boolean): Promise<IUser> {
    const formData = new FormData()
    formData.append('_method', 'PUT')
    formData.append('name', data.name)
    formData.append('email', data.email)
    if (updatePassword && data.password && data.password_confirmation) {
      formData.append('password', data.password)
      formData.append('password_confirmation', data.password_confirmation)
    }
    if (data.picture instanceof File) {
      formData.append('picture', data.picture)
    }
    formData.append('user_group_id', data.user_group_id.toString())
    formData.append('is_senior', data.is_senior ? '1' : '0')

    for (const sector_id of data.sector_ids) {
      formData.append('sector_ids[]', sector_id.toString())
    }

    return apiService
      .post(`/user/${userId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => response.data)
  }

  async listRoles(): Promise<{ id: number; name: string }[]> {
    return apiService.get('/user/roles').then((response) => response.data)
  }

  async updateRoles(id: number, selectedUserGroup: number, selectedSectors: number[], is_senior: boolean) {
    return apiService
      .put(`/user/${id}/roles`, { selectedUserGroup, selectedSectors, is_senior })
      .then((response) => response.data)
  }

  async restrict(id: number) {
    return apiService.post(`/user/restrict/${id}`).then((response) => response.data)
  }

  async unrestrict(id: number) {
    return apiService.post(`/user/unrestrict/${id}`).then((response) => response.data)
  }

  async destroy(id: number) {
    return apiService.delete(`/user/${id}`).then((response) => response.data)
  }

  async getManagers(): Promise<IUser[]> {
    return apiService.get('/user/managers').then((response) => response.data)
  }

  async me(): Promise<IMeData> {
    return apiService.get('/me').then((response) => response.data)
  }

  async getSectors(): Promise<ISector[]> {
    return apiService.get('/user/sectors').then((response) => response.data)
  }
}
